// Visible summary of every active filter on the Orders list. Sits
// above the search row so the operator can tell at a glance which
// filters are active without having to open the filter drawer (the
// drawer's badge only shows the count, not the values).
//
// Each chip = "<label>: <value>" + an X icon that removes that single
// filter. A "Clear all" link wipes everything. The component is purely
// presentational — the parent owns the query state and re-renders when
// `onChange(newQuery)` is called.
//
// Used by:
//   - Pages/PartnerDetail.jsx (Orders tab)
//   - Pages/Orders.jsx (global Orders list)
//
// Lookups (siteId → site name, driverId → driver name) happen on the
// fly via the passed-in `lookups` maps; falls back to the raw id as
// the chip label when no lookup is provided.

import React, { useEffect, useMemo, useState } from 'react';
import { Tag, Button } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { getMySites, listSitesForPartner } from '../../../modules/sites/api';

// Map a filter key + value to a human-friendly chip label. Returns
// `null` to skip rendering (e.g. for keys with empty/null values).
function buildChipLabel(key, value, lookups, t, tc) {
  if (value == null || value === '' || (Array.isArray(value) && value.length === 0)) return null;
  switch (key) {
    case 'name':
      return { label: t('name'), display: value };
    case 'pickupName':
      return { label: t('pickupName'), display: value };
    case 'routeId':
      return { label: 'Route ID', display: value };
    case 'partnerOrderRef':
      return { label: 'Partner Ref', display: value };
    case 'orderRef':
      return { label: 'Order Ref', display: value };
    case 'status':
      return { label: 'Status', display: tc(`orderStatuses.${value}`, value) };
    case 'deliveryDateFrom':
      return { label: 'From', display: value };
    case 'deliveryDateTo':
      return { label: 'To', display: value };
    case 'driverId': {
      const name = lookups?.driversById?.[value];
      return { label: 'Driver', display: name || `#${value}` };
    }
    case 'partnerId': {
      const name = lookups?.partnersById?.[value];
      return { label: 'Partner', display: name || `#${value}` };
    }
    case 'orderType':
      return { label: 'Order Type', display: tc(`orderTypes.${value}`, value) };
    case 'issueTypes': {
      const ids = typeof value === 'string' ? value.split(',').filter(Boolean) : value;
      const display = ids.map((v) => tc(`issueTypes.${v}`, v)).join(', ');
      return { label: 'Issues', display };
    }
    case 'partnerItemTypeIds': {
      const ids = typeof value === 'string' ? value.split(',').filter(Boolean) : value;
      const display = ids.map((id) => lookups?.itemTypesById?.[id] || `#${id}`).join(', ');
      return { label: 'Product Type', display };
    }
    case 'siteId': {
      const name = lookups?.sitesById?.[value];
      return { label: 'Site', display: name || `#${value}` };
    }
    default:
      return null; // pageSize, current, q etc. — not user-facing filters
  }
}

export default function ActiveOrderFiltersBar({
  query,
  onChange,
  partnerId, // when set, resolves siteId → site name via /partners/:id/sites
  viewOnly, // #159 — partner portal: /partners/me/sites instead
  driversById,
  partnersById,
  itemTypesById,
}) {
  const { t } = useTranslation('orders');
  const { t: tc } = useTranslation('common');

  // On-demand Site lookup. We fetch the catalog ONCE when there's an
  // active siteId filter AND a partnerId in scope. Cheap (a tens-of-
  // rows fetch) and the result is reused across re-renders via state.
  const [sitesById, setSitesById] = useState({});
  useEffect(() => {
    if (!query?.siteId || !partnerId) return;
    let cancelled = false;
    (viewOnly ? getMySites() : listSitesForPartner(partnerId)).then(({ response }) => {
      if (cancelled || !Array.isArray(response)) return;
      const map = {};
      for (const s of response) map[String(s.id)] = s.name;
      setSitesById(map);
    });
    return () => {
      cancelled = true;
    };
  }, [query?.siteId, partnerId, viewOnly]);

  const lookups = useMemo(
    () => ({ sitesById, driversById, partnersById, itemTypesById }),
    [sitesById, driversById, partnersById, itemTypesById],
  );

  // Build the list of chips. We deliberately iterate query keys in a
  // stable order (object property order) so chip placement doesn't
  // jump on re-render.
  const chips = useMemo(() => {
    if (!query) return [];
    const out = [];
    for (const key of Object.keys(query)) {
      const chip = buildChipLabel(key, query[key], lookups, t, tc);
      if (chip) out.push({ key, ...chip });
    }
    return out;
  }, [query, lookups, t, tc]);

  if (chips.length === 0) return null;

  const removeKey = (key) => {
    const next = { ...query };
    delete next[key];
    next.current = 1;
    onChange(next);
  };
  const clearAll = () => {
    onChange({ current: 1 });
  };

  return (
    <div className="active-filters-bar">
      <span className="active-filters-label">Active filters:</span>
      {chips.map((c) => (
        <Tag
          key={c.key}
          color="blue"
          closable
          closeIcon={<CloseOutlined />}
          onClose={(e) => {
            e.preventDefault();
            removeKey(c.key);
          }}
          style={{ marginInlineEnd: 0 }}
        >
          <span style={{ fontWeight: 500 }}>{c.label}:</span> {c.display}
        </Tag>
      ))}
      <Button type="link" size="small" onClick={clearAll} style={{ marginLeft: 'auto' }}>
        Clear all
      </Button>
    </div>
  );
}
